import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TimeSlotGroup } from "../store/types";

interface CalendarProps {
  timeSlotGroup: TimeSlotGroup;
  onBookSlot: (date: string) => void;
  selectedDate?: string | null;
  disabled?: boolean;
  onMonthChange?: (month: number, year: number) => void;
}

export const Calendar: React.FC<CalendarProps> = ({
  timeSlotGroup,
  onBookSlot,
  selectedDate: selectedDateProp,
  disabled = false,
  onMonthChange,
}) => {
  const { i18n } = useTranslation();
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  // Get days in month
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Format date to match the availableSlots key format (YYYY-MM-DD)
  // Using local date components to avoid timezone conversion issues
  const formatDate = (day: number, month: number, year: number) => {
    const monthStr = String(month + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    return `${year}-${monthStr}-${dayStr}`;
  };

  // Check if date is in the past
  const isPastDate = (day: number, month: number, year: number) => {
    const date = new Date(year, month, day);
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    return date < todayStart;
  };

  // Check if date is today
  const isToday = (day: number, month: number, year: number) => {
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  // Check if date has available slots
  const hasAvailableSlots = (day: number, month: number, year: number) => {
    const dateKey = formatDate(day, month, year);
    return (
      timeSlotGroup.availableSlots[dateKey] &&
      timeSlotGroup.availableSlots[dateKey].length > 0
    );
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
      onMonthChange?.(11, currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
      onMonthChange?.(currentMonth - 1, currentYear);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
      onMonthChange?.(0, currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
      onMonthChange?.(currentMonth + 1, currentYear);
    }
  };

  // Check if we can go to previous month
  const canGoPrevious = () => {
    if (currentYear > today.getFullYear()) return true;
    if (currentYear === today.getFullYear() && currentMonth > today.getMonth())
      return true;
    return false;
  };

  // Handle day click
  const handleDayClick = (day: number) => {
    if (disabled) return;
    if (isPastDate(day, currentMonth, currentYear)) return;
    if (!hasAvailableSlots(day, currentMonth, currentYear)) return;

    const dateKey = formatDate(day, currentMonth, currentYear);
    onBookSlot(dateKey);
  };

  const monthNames = useMemo(() => {
    const months = i18n.t("calendar.months", { returnObjects: true }) as unknown;
    return (Array.isArray(months) ? months : []) as string[];
  }, [i18n.language]);

  const daysOfWeek = useMemo(() => {
    const days = i18n.t("calendar.daysShort", { returnObjects: true }) as unknown;
    return (Array.isArray(days) ? days : []) as string[];
  }, [i18n.language]);

  // Generate calendar grid
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const past = isPastDate(day, currentMonth, currentYear);
      const today = isToday(day, currentMonth, currentYear);
      const available = hasAvailableSlots(day, currentMonth, currentYear);
      const clickable = !disabled && !past && available;
      const dateKey = formatDate(day, currentMonth, currentYear);
      const isSelected = selectedDateProp === dateKey;

      days.push(
        <div
          key={day}
          className={`calendar-day ${past ? "past" : ""} ${
            today ? "today" : ""
          } ${available ? "available" : ""} ${clickable ? "clickable" : ""} ${
            isSelected ? "selected" : ""
          } ${disabled ? "disabled" : ""}`}
          onClick={() => clickable && handleDayClick(day)}
        >
          <span className="day-number">{day}</span>
          {available && <span className="availability-dot"></span>}
        </div>
      );
    }

    return days;
  };

  return (
    <div className={`calendar-container ${disabled ? "disabled" : ""}`}>
      <div className="calendar-header">
        <button
          className="nav-button"
          onClick={goToPreviousMonth}
          disabled={!canGoPrevious() || disabled}
          aria-label="Previous month"
        >
          ‹
        </button>
        <h2 className="month-title">
          {monthNames[currentMonth]} {currentYear}
        </h2>
        <button
          className="nav-button"
          onClick={goToNextMonth}
          disabled={disabled}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="calendar-grid">
        {daysOfWeek.map((day) => (
          <div key={day} className="day-label">
            {day}
          </div>
        ))}
        {generateCalendarDays()}
      </div>

      <style>{`
        .calendar-container {
          width: 100%;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: opacity 0.3s ease;
        }

        .calendar-container.disabled {
          opacity: 0.6;
          pointer-events: none;
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 0 10px;
        }

        .month-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .nav-button {
          background: #f0f0f0;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          font-size: 1.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #333;
        }

        .nav-button:hover:not(:disabled) {
          background: #e0e0e0;
          transform: scale(1.05);
        }

        .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 8px;
        }

        .day-label {
          text-align: center;
          font-weight: 600;
          color: #666;
          padding: 10px 0;
          font-size: 0.875rem;
        }

        .calendar-day {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          position: relative;
          transition: all 0.2s ease;
          background: #f9f9f9;
          border: 2px solid transparent;
        }

        .calendar-day.empty {
          background: transparent;
        }

        .calendar-day.today {
          border-color: #fbbf24;
          background: #fef3c7;
        }

        .calendar-day.past {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .calendar-day.available {
          background: #eff6ff;
          border-color: #3b82f6;
        }

        .calendar-day.selected {
          background: #3b82f6;
          border-color: #2563eb;
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .calendar-day.selected .day-number {
          color: white;
          font-weight: 600;
        }

        .calendar-day.selected .availability-dot {
          background: white;
        }

        .calendar-day.clickable {
          cursor: pointer;
        }

        .calendar-day.clickable:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          background: #dbeafe;
        }

        .calendar-day.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .day-number {
          font-size: 0.95rem;
          font-weight: 500;
          color: #1a1a1a;
        }

        .calendar-day.past .day-number {
          color: #999;
        }

        .availability-dot {
          position: absolute;
          bottom: 4px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #3b82f6;
        }

        .calendar-day.today .availability-dot {
          background: #f59e0b;
        }

        @media (max-width: 600px) {
          .calendar-container {
            padding: 15px;
          }

          .month-title {
            font-size: 1.25rem;
          }

          .nav-button {
            width: 35px;
            height: 35px;
            font-size: 1.25rem;
          }

          .calendar-grid {
            gap: 4px;
          }

          .day-label {
            font-size: 0.75rem;
            padding: 8px 0;
          }

          .day-number {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </div>
  );
};
